import { useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '../contexts/AuthContext';
import { projectService } from '../services/projectService';
import { EmployeeProjectModelList } from './EmployeeProjectModelList';

export const ProjectSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';

  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem('project-settings-selected-project-id') || '';
  });

  const {
    data: projectsResponse,
    isLoading: isLoadingProjects,
    error: projectsError,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: ['projects', 'by-role'],
    queryFn: () => (isAdmin ? projectService.getProjects() : projectService.getProjectsByRole()),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const projects = projectsResponse || [];

  useEffect(() => {
    if (projects.length > 0) {
      if (!selectedProjectId || !projects.some((p) => p.id === selectedProjectId)) {
        const defaultId = projects[0].id;
        setSelectedProjectId(defaultId);
        localStorage.setItem('project-settings-selected-project-id', defaultId);
      }
    } else if (selectedProjectId) {
      setSelectedProjectId('');
      localStorage.removeItem('project-settings-selected-project-id');
    }
  }, [projects, selectedProjectId]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    localStorage.setItem('project-settings-selected-project-id', projectId);
  };

  if (isLoadingProjects) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-lg bg-surface-card p-8 shadow-sm">
        <div className="flex flex-col items-center gap-4 text-text-secondary">
          <Spinner className="size-10" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 rounded-lg bg-surface-card p-8 text-danger-600 shadow-sm">
        <p>
          Error loading data:{' '}
          {projectsError instanceof Error ? projectsError.message : 'Unknown error'}
        </p>
        <Button type="button" onClick={() => void refetchProjects()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <EmployeeProjectModelList
        selectedProjectId={selectedProjectId}
        onProjectChange={handleProjectChange}
        onEdit={() => {
          queryClient.invalidateQueries({ queryKey: ['employee-project-models'] });
        }}
        onDelete={() => {
          queryClient.invalidateQueries({ queryKey: ['employee-project-models'] });
        }}
        onAdd={() => {
          queryClient.invalidateQueries({ queryKey: ['employee-project-models'] });
        }}
      />
    </div>
  );
};
